// ─── Enums / Literals ─────────────────────────────────────────────────────────

export type ReconStatus =
  | "MATCHED"
  | "MISMATCH_PRICE"
  | "MISMATCH_MARKUP"
  | "MISMATCH_PRICE_AND_MARKUP"
  | "UNMATCHED_BGM"
  | "UNMATCHED_ACERS"
  | "CANON_FAILED"
  | "FAILED_MISSING_CONTRACT_MAPPING"
  | "FAILED_MISSING_CLIENT_MAPPING"
  | "SUCCESS";

export const ReconStatusEnum = {
  MATCHED: "MATCHED",
  MISMATCH_PRICE: "MISMATCH_PRICE",
  MISMATCH_MARKUP: "MISMATCH_MARKUP",
  MISMATCH_PRICE_AND_MARKUP: "MISMATCH_PRICE_AND_MARKUP",
  UNMATCHED_BGM: "UNMATCHED_BGM",
  UNMATCHED_ACERS: "UNMATCHED_ACERS",
  CANON_FAILED: "CANON_FAILED",
  FAILED_MISSING_CONTRACT_MAPPING: "FAILED_MISSING_CONTRACT_MAPPING",
  FAILED_MISSING_CLIENT_MAPPING: "FAILED_MISSING_CLIENT_MAPPING",
  SUCCESS: "SUCCESS",
} as const satisfies Record<string, ReconStatus>;

export type SideLabel = "ACERS" | "BGM";

export const ReconSourceEnum = {
  ACERS: "ACERS",
  BGM: "BGM",
} as const satisfies Record<string, SideLabel>;

export type BuySell = "BUY" | "SELL";

export type ActiveTab =
  | "all"
  | "matched"
  | "mismatches"
  | "unmatched"
  | "failures";

export const ACCEPTED_EXTENSIONS = [".xlsx", ".xlsm", ".csv"];

export const ActiveTabEnum = {
  ALL: "all",
  MATCHED: "matched",
  MISMATCHES: "mismatches",
  UNMATCHED: "unmatched",
  FAILURES: "failures",
} as const satisfies Record<string, ActiveTab>;
// ─── Filter / Request ─────────────────────────────────────────────────────────
// API accepts: file, source, start_date, end_date

export interface ReconRunPayload {
  source: string;     // broker/source — sent as "source" field
  start_date: string; // "YYYY-MM-DD"
  end_date: string;   // "YYYY-MM-DD"
  file: File;         // CSV / XLS uploaded file
  contract?: string;
  buy_sell?: BuySell;
}

export interface ReconReRunPayload {
  source: string;
  start_date: string;
  end_date: string;
  contract?:string
  buy_sell?:BuySell
  // No file — reuses previously uploaded file on backend
}

// ─── Filter Form State ────────────────────────────────────────────────────────

export interface ReconFilterFormData {
  source: string;
  start_date: string;
  end_date: string;
  contract:string;
  buy_sell:string;
}

// ─── Summary ──────────────────────────────────────────────────────────────────

export interface ReconSummary {
  total_bgm_trades: number;
  total_acers_trades: number;
  matched: number;
  mismatch_price: number;
  mismatch_markup: number;
  mismatch_price_and_markup: number;
  unmatched_bgm: number;
  unmatched_acers: number;
  canon_failed: number;
}

export interface BreakReasonItem {
  row: number;
  value: string;
  reason: string;
}

// ─── Result Rows ──────────────────────────────────────────────────────────────

export interface ReconTradeRow {
  id: string;
  trade_id: string;
  source_trade_id: string | null;
  trade_date: string;
  source_system: string;
  client: string;
  contract: string;
  contract_month: string;
  direction: string;
  price: number;
  quantity: number;
  bgm_price: number | null;
  acers_price: number | null;
  markup_bgm: number | null;
  markup_acers: number | null;
  reconciliation_status: ReconStatus;
  canonicalisation_status: ReconStatus;
  break_reason: BreakReasonItem[] | string | string[] | null;
  edit_id: string | null;
  issue?: string;
  matched_with_id?: string; 
}


export interface SidedTableConfig {
  label: string;
  accent: string;
  bg: string;
  border: string;
  systemFilter: (r: ReconTradeRow) => boolean;
}

// ─── Full API Response ────────────────────────────────────────────────────────

export interface ReconRunResponse {
  run_id: string;
  summary: ReconSummary;
  results: ReconTradeRow[];
}

export interface SelectOption {
  value: string;
  label: string;
}